/**
 * Which tree rows are expanded (`060-notebook-tree.md` §5, "Expand/collapse persistence").
 *
 * localStorage, not the server: it is per-device UI preference, it changes on every
 * chevron click (syncing it would mean a write per click), and losing it costs the user
 * two clicks. It is read before first paint, which rules out IndexedDB. It holds nothing
 * but page ids — never user content (`112-offline-drafts.md` §2, "Where localStorage IS
 * still used").
 */
import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'rn.tree.expanded';

const listeners = new Set<() => void>();
let expanded: ReadonlySet<string> = read();

function read(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return new Set();

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();

    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function write(next: ReadonlySet<string>): void {
  expanded = next;
  localStorage.setItem(KEY, JSON.stringify([...next]));
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): ReadonlySet<string> {
  return expanded;
}

export function toggleExpanded(id: string): void {
  const next = new Set(expanded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  write(next);
}

export function setExpanded(id: string, open: boolean): void {
  if (expanded.has(id) === open) return;
  const next = new Set(expanded);
  if (open) next.add(id);
  else next.delete(id);
  write(next);
}

/** Auto-expand the ancestors of the page being opened — including on a direct URL load or
 *  a search hit. Opening a deep page must REVEAL it in the tree (§5). */
export function expandAll(ids: readonly string[]): void {
  if (ids.every((id) => expanded.has(id))) return;
  write(new Set([...expanded, ...ids]));
}

/** Pruned against the fetched tree on every load, so the key does not grow forever (§5). */
export function pruneExpanded(existing: ReadonlySet<string>): void {
  const kept = [...expanded].filter((id) => existing.has(id));
  if (kept.length === expanded.size) return;
  write(new Set(kept));
}

export function useExpanded(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function useToggleExpanded(): (id: string) => void {
  return useCallback((id: string) => {
    toggleExpanded(id);
  }, []);
}
